/* TODO: fix the eslint errors here */
/* eslint-disable */
import { initialize as initializeWebSdk } from '@pendo/web-sdk';
/**
 * A list of apps that use pendo tracking.
 * These names should resemble the repo's name as much as it makes sense.
 */
type Apps =
  | 'frontend/weave'
  | 'platform-webapp-customer'
  | 'desktop-client'
  | 'desktop-client-notifications'
  | 'forms-portal-ui'
  | 'payments-webapp-portal'
  | 'anonymous-users'
  | 'frontend/weave-pay'
  | 'frontend/notifications'
  | 'frontend/booking-site';

/**
 * Pendo's types are relatively loose because they don't know what you will want to track.
 * The purpose of WeavePendoPayload is to make the types strict for our use case
 * and help this package's consumer know what options the functions accept
 */
type WeavePendoPayload = {
  accountId?: string;
  apiKey?: string;
  excludeAllText?: boolean;
  visitorApp: Apps;
  visitorClientVersion?: string;
  visitorFeatureFlags?: any;
  visitorId?: string;
  visitorUsername?: string;
  visitorJobTitles?: any;
  visitorLanguage?: string;
  visitorRole?: string;
  visitorShellVersion?: string;
  visitorShellFlavor?: string;
  visitorIsShell?: boolean;
  browserWidth?: number;
  browserHeight?: number;
  accountName?: string;
  accountStatus?: string;
  officeType?: string;
  productBundle?: string;
  beta?: boolean;
  pmsList?: string;
  customizationFlagsActive?: string[];
  customizationFlagsHidden?: string[];
  customizationFlagsTrial?: string[];
  customizationFlagsFreemium?: string[];
};

type PendoEnvironment = 'io' | 'eu' | 'jpn' | 'us1' | 'au' | 'gov';

type Pendo = {
  identify: (
    props: Pick<
      WeavePendoPayload,
      | 'visitorId'
      | 'visitorRole'
      | 'visitorLanguage'
      | 'accountId'
      | 'visitorApp'
      | 'visitorUsername'
      | 'browserWidth'
      | 'browserHeight'
      | 'accountName'
      | 'accountStatus'
      | 'officeType'
      | 'productBundle'
      | 'beta'
      | 'pmsList'
      | 'customizationFlagsActive'
      | 'customizationFlagsHidden'
      | 'customizationFlagsTrial'
      | 'customizationFlagsFreemium'
    >
  ) => void;
  /**
   * initWithCallback is copied code from the pendo.script method.
   * since pendo does not provide an event to reliably inform you when it has finished loading,
   * we pulled out the relevant code and added a `load` eventListener that accepts a callback function.
   * this way we can run `pendo.initialize()` after the script has finished loading.
   */
  initWithCallback: (apiKey: string, callbackFunction: () => void, env?: string) => void;
  initWithScript: (args: {
    apiKey: string;
    domTag?: string;
    scriptElementId?: string;
    runInitialize?: boolean;
  }) => void;
  initialize: (props: WeavePendoPayload) => void;
  isReady: () => boolean;
  loadGuides: () => void;
  script: (apiKey: string) => string;
  showGuideByName: (name: string) => void;
  showGuideById: (id: string) => void;
  startGuides: () => void;
  track: (trackType: string, metadata?: pendo.Metadata) => void;
  disableGuides: () => void;
  enableGuides: () => void;
  /**
   * dom selectors used to customize the behavior of the resource center,
   * so that the third list item triggers the ada chat bot immediately
   */
  domSelectors: { triggerId: string; guideListId: string };
  updateOptions: (props: Omit<WeavePendoPayload, 'accountId' | 'apiKey' | 'excludeAllText'>) => void;
};

/**
 * Because there are several options for pendo configuration, it's possible that
 * some fields will go unused; for example `shellVersion` is unique to the desktop client.
 * We don't want to track unused values in pendo if we don't need to. This function makes sure only
 * truthy values get passed into pendo.
 */
const getTruthyValues = (obj: Record<string, any>) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (!!value) return { ...acc, [key]: value };
    return acc;
  }, {});
};

let pendoInitConfig: { apiKey: string; env: PendoEnvironment } | null = null;
let pendoReadyPromise: Promise<any> | null = null;
const pendingOperations: Array<() => void> = [];

const ALLOWED_PENDO_ENVS: PendoEnvironment[] = ['io', 'eu', 'jpn', 'us1', 'au', 'gov'];

const normalizePendoEnv = (env?: string): PendoEnvironment => {
  if (!env) return 'io';
  if (ALLOWED_PENDO_ENVS.includes(env as PendoEnvironment)) {
    return env as PendoEnvironment;
  }
  console.warn(`Invalid Pendo env "${env}". Falling back to "io".`);
  return 'io';
};

const enqueueOrRun = (operation: () => void) => {
  if (window?.pendo) {
    operation();
    return;
  }
  pendingOperations.push(operation);
};

const flushPendingOperations = () => {
  while (pendingOperations.length > 0) {
    const operation = pendingOperations.shift();
    operation?.();
  }
};

const getVisitorPayload = ({
  visitorApp,
  visitorClientVersion = '',
  visitorFeatureFlags = undefined,
  visitorId = '',
  visitorJobTitles = undefined,
  visitorLanguage = 'en_US',
  visitorRole = '',
  visitorShellVersion = '',
  visitorShellFlavor = '',
  visitorIsShell = false,
  visitorUsername = '',
  browserWidth = 0,
  browserHeight = 0,
}: WeavePendoPayload) => {
  return getTruthyValues({
    app: visitorApp,
    clientVersion: visitorClientVersion,
    featureFlags: visitorFeatureFlags,
    id: visitorId,
    jobTitles: visitorJobTitles,
    language: visitorLanguage,
    role: visitorRole,
    shellVersion: visitorShellVersion,
    shellFlavor: visitorShellFlavor,
    isShell: visitorIsShell,
    username: visitorUsername,
    browserWidth,
    browserHeight,
  });
};

const getAccountPayload = ({
  accountId = '',
  accountName = '',
  accountStatus = '',
  officeType = '',
  productBundle = '',
  beta = false,
  pmsList = '',
  customizationFlagsActive,
  customizationFlagsHidden,
  customizationFlagsTrial,
  customizationFlagsFreemium,
}: Partial<WeavePendoPayload>) => {
  return getTruthyValues({
    id: accountId,
    accountName,
    accountStatus,
    officeType,
    productBundle,
    beta,
    pmsList,
    customizationFlagsActive,
    customizationFlagsHidden,
    customizationFlagsTrial,
    customizationFlagsFreemium,
  });
};

const ensurePendoInitialized = (props?: WeavePendoPayload) => {
  if (pendoReadyPromise) {
    return pendoReadyPromise;
  }

  if (window?.pendo) {
    pendoReadyPromise = Promise.resolve(window.pendo);
    flushPendingOperations();
    return pendoReadyPromise;
  }

  const apiKey = pendoInitConfig?.apiKey ?? props?.apiKey ?? '';
  const env = pendoInitConfig?.env ?? 'io';

  if (!apiKey) {
    return Promise.reject(new Error('Pendo initialization failed: missing api key.'));
  }

  pendoReadyPromise = (
    initializeWebSdk({
      apiKey,
      env,
      globalKey: 'pendo',
      excludeAllText: props?.excludeAllText ?? true,
      visitor: props ? getVisitorPayload(props) : undefined,
      account: props ? getAccountPayload(props) : undefined,
    }) as Promise<any>
  )
    .then((instance) => {
      flushPendingOperations();
      return instance;
    })
    .catch((error) => {
      pendoReadyPromise = null;
      throw error;
    });

  return pendoReadyPromise;
};

/**
 * One stop shop for all things pendo in weave apps.
 * Validate pendo installed correctly by running pendo.validateInstall() in the developer console
 */
export const pendo: Pendo = {
  identify: ({
    accountId = '',
    visitorApp,
    visitorId = '',
    visitorLanguage = 'en_US',
    visitorRole = '',
    visitorUsername = '',
    browserWidth = 0,
    browserHeight = 0,
    accountName = '',
    accountStatus = '',
    officeType = '',
    productBundle = '',
    beta = false,
    pmsList = '',
    customizationFlagsActive,
    customizationFlagsHidden,
    customizationFlagsTrial,
    customizationFlagsFreemium,
  }) => {
    enqueueOrRun(() => {
      window?.pendo?.identify({
        visitor: getTruthyValues({
          id: visitorId,
          app: visitorApp,
          language: visitorLanguage,
          role: visitorRole,
          username: visitorUsername,
          browserWidth,
          browserHeight,
        }),
        account: getTruthyValues({
          id: accountId,
          accountName: accountName,
          accountStatus: accountStatus,
          officeType: officeType,
          productBundle: productBundle,
          beta: beta,
          pmsList: pmsList,
          customizationFlagsActive,
          customizationFlagsHidden,
          customizationFlagsTrial,
          customizationFlagsFreemium,
        }),
      });
    });
  },
  initWithScript: ({ apiKey, domTag = 'body', scriptElementId = 'pendo-loader', runInitialize = false }) => {
    const scriptElement = document.createElement('script');
    scriptElement.innerHTML = pendo.script(apiKey);
    scriptElement.id = scriptElementId;
    const tag = document.getElementsByTagName(domTag)?.[0];
    tag?.appendChild(scriptElement);
    if (runInitialize) window?.pendo?.initialize({ excludeAllText: true });
  },
  initialize: ({
    accountId = '',
    apiKey = '',
    excludeAllText = true,
    visitorApp,
    visitorClientVersion = '',
    visitorFeatureFlags = undefined,
    visitorId = '',
    visitorJobTitles = undefined,
    visitorLanguage = 'en_US',
    visitorRole = '',
    visitorShellVersion = '',
    visitorShellFlavor = '',
    visitorIsShell = false,
    browserWidth = 0,
    browserHeight = 0,
  }) => {
    if (window?.pendo) {
      window.pendo.initialize({
        apiKey,
        excludeAllText,
        // `visitor` may be expanded over time to track more user information
        visitor: getTruthyValues({
          app: visitorApp,
          clientVersion: visitorClientVersion,
          featureFlags: visitorFeatureFlags,
          id: visitorId,
          jobTitles: visitorJobTitles,
          language: visitorLanguage,
          role: visitorRole,
          shellVersion: visitorShellVersion,
          isShell: visitorIsShell,
          shellFlavor: visitorShellFlavor,
          browserWidth,
          browserHeight,
        }),
        account: {
          id: accountId,
        },
      });
      return;
    }

    ensurePendoInitialized({
      accountId,
      apiKey,
      excludeAllText,
      visitorApp,
      visitorClientVersion,
      visitorFeatureFlags,
      visitorId,
      visitorJobTitles,
      visitorLanguage,
      visitorRole,
      visitorShellVersion,
      visitorShellFlavor,
      visitorIsShell,
      browserWidth,
      browserHeight,
    }).catch((error) => {
      console.error('Pendo initialize failed', error);
    });
  },
  isReady: () => window?.pendo?.isReady() || false,
  loadGuides: () => {
    return window?.pendo?.loadGuides();
  },
  /**
   * initWithCallback is copied code from the pendo.script method.
   * since pendo does not provide an event to reliably inform you when it has finished loading,
   * we pulled out the relevant code and added a `load` eventListener that accepts a callback function.
   * this way we can run `pendo.initialize()` after the script has finished loading.
   */
  initWithCallback: (apiKey, callbackFunction, env = 'io') => {
    pendoInitConfig = { apiKey, env: normalizePendoEnv(env) };

    if (window?.pendo) {
      callbackFunction();
      return;
    }

    queueMicrotask(() => {
      callbackFunction();
    });
  },
  script: (apiKey) => `
    (function (apiKey) {
      (function (p, e, n, d, o) {
        var v, w, x, y, z; o = p[d] = p[d] || {}; o._q = [];
        v = ['initialize', 'identify', 'updateOptions', 'pageLoad']; for (w = 0, x = v.length; w < x; ++w)(function (m) {
          o[m] = o[m] || function () { o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0))); };
        })(v[w]);
        y = e.createElement(n); y.async = !0; y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
        z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
      })(window, document, 'script', 'pendo');
    })('${apiKey}');
  `,
  showGuideByName: (name) => {
    window?.pendo?.showGuideByName(name);
  },
  showGuideById: (id) => {
    window?.pendo?.showGuideById(id);
  },
  startGuides: () => {
    window?.pendo?.loadGuides();
  },
  disableGuides: () => {
    window?.pendo?.setGuidesDisabled?.(true);
    window?.pendo?.stopGuides?.();
  },
  enableGuides: () => {
    window?.pendo?.setGuidesDisabled?.(false);
    window?.pendo?.startGuides?.();
  },
  track: (trackType, metadata) => {
    enqueueOrRun(() => {
      window?.pendo?.track?.(trackType, metadata);
    });
  },
  // these dom selectors are used to customize the behavior of the resource center,
  // so that the third list item triggers the ada chat bot immediately
  domSelectors: {
    triggerId: '_pendo-badge_Fl_aptzXI1_eyDXK5bRWq3wTl28',
    guideListId: 'pendo-list-fe46d14e',
  },
  updateOptions: ({
    visitorClientVersion = '',
    visitorFeatureFlags = undefined,
    visitorId = '',
    visitorJobTitles = undefined,
    visitorLanguage = 'en_US',
    visitorRole = '',
    visitorShellVersion = '',
  }) => {
    // updateOptions only tracks visitor options
    enqueueOrRun(() => {
      window?.pendo?.updateOptions({
        visitor: getTruthyValues({
          clientVersion: visitorClientVersion,
          featureFlags: visitorFeatureFlags,
          id: visitorId,
          jobTitles: visitorJobTitles,
          language: visitorLanguage,
          role: visitorRole,
          shellVersion: visitorShellVersion,
        }),
      });
    });
  },
};
